import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { AccessoriesGuard } from '@rci/self-serve-fido/accessories/accessories.guard';
import {
  ACCESSORIES_ROUTE,
  DATA_MANAGEMENT_ROUTE,
  MAINTENANCE_ROUTE,
  OVERVIEW_ROUTE,
  PTP_URL,
  REPORT_PAYMENT_URL,
  ROGERS_BANK_ROUTE,
  VIEW_BILL_URL,
  WIRELESS_ROUTE,
} from '@rci/self-serve-fido/app.constant';
import { NotFoundComponent } from '@rci/self-serve-fido/core/not-found/not-found.component';
import { SigninGuard } from '@rci/self-serve-fido/core/signin/signin.guard';
import { GroupIdOrSubscriberDeeplinkGuard } from '@rci/self-serve-fido/guard/group-id-or-subscriber-deeplink.guard';
import { EnsureNoGlobalApiErrorGuard } from '@rci/self-serve-fido/shared/error/ensure-no-global-api-error.guard';
import {
  commonGuards,
  getWirelessDeeplinkGuard,
  selectActiveNonPrepaidAccountGuard,
} from '@rci/self-serve-fido/guard/routing.guards';
import { SubscriberDeeplinkGuard } from '@rci/self-serve-fido/guard/subscriber-deeplink.guard';
import { RogerBankBlankAppGuard } from './rogers-bank/blank-app/rogers-bank-blank-app.guard';
import { hasUserPermissionGuard } from '@rogers/self-serve-common/user-permission';
import { selectAccountGuard } from '@rci/self-serve-fido/shared/account-selector-modal/select-account.guard';

const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    redirectTo: OVERVIEW_ROUTE,
  },
  {
    path: OVERVIEW_ROUTE,
    loadChildren: () => import('./overview/overview.module').then(m => m.OverviewModule),
    canActivate: commonGuards,
  },
  {
    path: WIRELESS_ROUTE,
    loadChildren: () => import('./wireless/wireless.module').then(m => m.WirelessModule),
    canActivate: getWirelessDeeplinkGuard(WIRELESS_ROUTE),
  },
  {
    path: 'change-phone-number',
    loadChildren: () => import('./change-phone-number/change-phone-number.module').then(m => m.ChangePhoneNumberModule),
    canActivate: getWirelessDeeplinkGuard('change-phone-number'),
  },
  {
    path: 'profile-settings',
    loadChildren: () => import('./profile-settings/profile-settings.module').then(m => m.ProfileSettingsModule),
    canActivate: commonGuards,
  },
  {
    path: 'change-voicemail-password',
    loadChildren: () => import('./change-voicemail/change-voicemail.module').then(m => m.ChangeVoicemailAppModule),
    canActivate: getWirelessDeeplinkGuard('change-voicemail-password'),
  },
  {
    path: 'temporary-suspension',
    loadChildren: () =>
      import('./temporary-suspension/temporary-suspension.module').then(m => m.TemporarySuspensionAppModule),
    canActivate: commonGuards,
  },
  {
    path: 'change-caller-id',
    loadChildren: () => import('./change-caller-id/change-caller-id.module').then(m => m.ChangeCallerIdAppModule),
    canActivate: [...getWirelessDeeplinkGuard('change-caller-id'), GroupIdOrSubscriberDeeplinkGuard],
  },
  {
    path: 'manage-sim',
    loadChildren: () => import('./manage-sim/manage-sim-app.module').then(m => m.ManageSimAppModule),
    canActivate: [...getWirelessDeeplinkGuard('manage-sim'), SubscriberDeeplinkGuard],
  },
  {
    path: 'link-account',
    loadChildren: () => import('./link-account/link-account.module').then(m => m.LinkAccountAppModule),
    canActivate: [SigninGuard, EnsureNoGlobalApiErrorGuard],
  },
  {
    path: 'offers',
    loadChildren: () => import('./my-offers/my-offers-app.module').then(m => m.MyOffersAppModule),
    canActivate: [...commonGuards, selectActiveNonPrepaidAccountGuard],
  },
  {
    path: 'transfer-phone-number',
    loadChildren: () => import('./port-in/port-in.module').then(m => m.PortInAppModule),
    canActivate: [...getWirelessDeeplinkGuard('transfer-phone-number'), GroupIdOrSubscriberDeeplinkGuard],
  },
  {
    path: 'retrieve-puk',
    loadChildren: () => import('./wireless/puk-code/puk-code.module').then(m => m.PukCodeModule),
    canActivate: getWirelessDeeplinkGuard('retrieve-puk'),
  },
  {
    path: 'orders',
    loadChildren: () => import('./my-orders/my-orders-app.module').then(m => m.MyOrdersAppModule),
    canActivate: [...commonGuards, selectAccountGuard],
  },
  {
    path: DATA_MANAGEMENT_ROUTE,
    loadChildren: () => import('./wireless/data-management/data-management.module').then(m => m.DataManagementModule),
    canActivate: commonGuards,
  },
  {
    path: PTP_URL,
    loadChildren: () => import('./promise-to-pay/promise-to-pay.module').then(m => m.PromiseToPayModule),
    canActivate: commonGuards,
  },
  {
    path: REPORT_PAYMENT_URL,
    loadChildren: () => import('./report-payment/report-payment.module').then(m => m.ReportPaymentModule),
    canActivate: commonGuards,
  },
  {
    path: ACCESSORIES_ROUTE,
    loadChildren: () => import('./accessories/accessories.module').then(m => m.AccessoriesModule),
    canActivate: [...commonGuards, AccessoriesGuard],
  },
  {
    path: 'payments',
    loadChildren: () => import('./payments/payments.module').then(m => m.PaymentsModule),
    canActivate: commonGuards,
  },
  {
    path: 'repair-claim',
    loadChildren: () => import('./repair-claim/repair-claim-app.module').then(m => m.RepairClaimAppModule),
    canActivate: [SigninGuard],
  },
  {
    path: ROGERS_BANK_ROUTE,
    loadChildren: () => import('./rogers-bank/rogers-bank.module').then(m => m.RogersBankModule),
    canActivate: [RogerBankBlankAppGuard, EnsureNoGlobalApiErrorGuard],
  },
  {
    path: VIEW_BILL_URL,
    loadChildren: () => import('./view-bill/view-bill.module').then(m => m.ViewBillModule),
    canActivate: commonGuards,
  },
  {
    path: MAINTENANCE_ROUTE,
    loadChildren: () => import('./maintenance/maintenance.module').then(m => m.MaintenanceModule),
  },
  { path: '**', component: NotFoundComponent },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { scrollPositionRestoration: 'enabled' })],
  exports: [RouterModule],
})
export class AppRoutingModule {}
